'use client';

/**
 * SysObeyaProductivityChartCard — per-module bar chart of Traditional
 * hours vs AI-Assisted hours. Slices `response.data[]` for the
 * `trad_hours` and `ai_hours` fields.
 *
 * Typography from `useTypographyBundle('canvas_dashboard')`:
 *   header   = card title (20px font-semibold)
 *   subtitle = card subtitle (14px muted)
 *   body     = row labels + values (14px)
 *   metadata = small captions / series toggle (12px muted)
 *
 * Container chrome (card padding, series toggle pill styling, bar
 * heights) is render concern, lives here. Chrome strings via
 * `response.labels`.
 *
 * @see klyde_edge/ui_config/labels/system_obeya.py — mod_sum_chart_*, mod_sum_series_*
 */

import { useState, useMemo } from 'react';
import { TrendingUp } from 'lucide-react';
import { cn } from '@/lib/utils/cn';
import { useCardTypography } from '@/lib/design/card-chrome';
import { useCardExport } from '@/lib/design/card-export';
import { useSystemObeyaView } from './use-system-obeya-view';

interface Props {
  view_name?: string;
}

interface SeriesDef {
  key: string;
  label_key: string;
}

const SERIES: SeriesDef[] = [
  { key: 'both', label_key: 'mod_sum_series_both' },
  { key: 'trad', label_key: 'mod_sum_series_traditional' },
  { key: 'ai',   label_key: 'mod_sum_series_ai' },
];

export function SysObeyaProductivityChartCard({ view_name = 'module_summary' }: Props) {
  const { data, isLoading, queryKey } = useSystemObeyaView(view_name);
  const T = useCardTypography();
  const L = data?.labels ?? {};
  const [seriesKey, setSeriesKey] = useState<string>('both');

  // Derived rows — computed unconditionally so the export memo below
  // has stable dependencies regardless of load state. When data hasn't
  // arrived, `rows` is just an empty array.
  const rows = ((data?.data ?? []) as Array<Record<string, unknown>>)
    .filter((r) => ((r.trad_hours as number) ?? 0) > 0 || ((r.ai_hours as number) ?? 0) > 0)
    .map((r) => ({
      name: (r.display_name as string) || (r.package as string) || '',
      trad: (r.trad_hours as number) ?? 0,
      ai: (r.ai_hours as number) ?? 0,
    }));

  const dash = L.dash_placeholder ?? '—';
  const hoursSuffix = L.unit_hours_short ?? '';
  const chartTitle = L.mod_sum_chart_title ?? '';

  // Card export — hook MUST be called every render. The export value
  // is empty when data isn't loaded; the hook ordering stays stable.
  useCardExport(useMemo(() => {
    const headerKey = L.mod_sum_col_module ?? 'Module';
    const tradKey = L.mod_sum_series_traditional ?? 'Traditional';
    const aiKey = L.mod_sum_series_ai ?? 'AI';
    const md = rows.length === 0
      ? ''
      : [
          `| ${headerKey} | ${tradKey} | ${aiKey} |`,
          `| --- | ---: | ---: |`,
          ...rows.map((r) => `| ${r.name || dash} | ${fmt(r.trad)}${hoursSuffix} | ${fmt(r.ai)}${hoursSuffix} |`),
        ].join('\n');
    return {
      label: chartTitle,
      text: rows.map((r) => `${r.name}: trad=${fmt(r.trad)}${hoursSuffix} ai=${fmt(r.ai)}${hoursSuffix}`).join('\n'),
      markdown: md,
      // Scene mirrors painted hours-with-suffix ("1,472h") — what
      // each bar label actually reads. Raw hours travel in the
      // capture envelope's `payloads` for the `format(...) == scene`
      // assertion to remain checkable.
      json: {
        rows: rows.map((r) => ({
          name: r.name,
          trad_hours: `${fmt(r.trad)}${hoursSuffix}`,
          ai_hours: `${fmt(r.ai)}${hoursSuffix}`,
        })),
      },
      content_key: 'sys_obeya_productivity_chart',
      surface: 'canvas',
      state: { series_key: seriesKey },
      // Status = paintable content, not the query flag.
      status: (data ? 'loaded' : 'loading') as 'loading' | 'loaded',
      query_key: queryKey,
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(rows), dash, hoursSuffix, chartTitle, JSON.stringify(L), seriesKey, data, isLoading, queryKey]));

  if (isLoading || !data) {
    return (
      <CardShell>
        <div className="p-6">
          <div className={T.body}>{L.loading_message ?? ''}</div>
        </div>
      </CardShell>
    );
  }

  const maxVal = rows.reduce((m, r) => Math.max(m, r.trad, r.ai), 1);

  return (
    <CardShell>
      <div className="p-6 flex flex-col h-full">
        <div className="flex items-start justify-between mb-6">
          <div>
            <div className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-primary" />
              <h2 className={T.header}>{L.mod_sum_chart_title ?? ''}</h2>
            </div>
            <p className={cn(T.subtitle, 'mt-1')}>{L.mod_sum_chart_subtitle ?? ''}</p>
          </div>
          <div className="flex items-center gap-1">
            {SERIES.map((s) => (
              <button
                key={s.key}
                onClick={() => setSeriesKey(s.key)}
                className={cn(
                  T.metadata,
                  'px-2 py-1 rounded-md transition-colors',
                  seriesKey === s.key
                    ? 'bg-primary/10 text-primary font-medium'
                    : 'hover:bg-muted hover:text-foreground',
                )}
              >
                {L[s.label_key] ?? ''}
              </button>
            ))}
          </div>
        </div>
        <div className="space-y-3 overflow-auto flex-1">
          {rows.map((row, idx) => (
            <div key={row.name || `row-${idx}`} className="space-y-1">
              <div className="flex items-center justify-between gap-3">
                <span className={cn(T.body, 'font-mono truncate flex-1')}>{row.name || dash}</span>
                <span className={cn(T.body, 'tabular-nums font-mono font-medium')}>
                  {seriesKey !== 'ai' && <span className="text-muted-foreground mr-3">{fmt(row.trad)}{hoursSuffix}</span>}
                  {seriesKey !== 'trad' && <span className="text-primary">{fmt(row.ai)}{hoursSuffix}</span>}
                </span>
              </div>
              <div className="flex gap-1 h-2.5">
                {seriesKey !== 'ai' && (
                  <div
                    className="bg-muted-foreground/40 rounded-sm"
                    style={{ width: `${(row.trad / maxVal) * 100}%` }}
                  />
                )}
                {seriesKey !== 'trad' && (
                  <div
                    className="bg-primary/60 rounded-sm"
                    style={{ width: `${(row.ai / maxVal) * 100}%` }}
                  />
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </CardShell>
  );
}

function CardShell({ children }: { children: React.ReactNode }) {
  return <div className="rounded-xl border border-border bg-card shadow-sm h-full overflow-hidden flex flex-col">{children}</div>;
}
function fmt(n: number): string {
  return n.toLocaleString();
}
